import usePdfPreview from "../hooks/usePdfPreview";
import useRecoverableDraft from "../hooks/useRecoverableDraft";
import LoadError from "../components/LoadError";
import Dialog from "../components/Dialog";
import { useConfirm } from "../components/ConfirmProvider";
import useUnsavedChanges from "../hooks/useUnsavedChanges";
import useActionLock from "../hooks/useActionLock";
import { formatMoney } from "../utils/format";
import BillingSummary from "../components/BillingSummary";
import { compatiblePrestations } from "../utils/billing";
import { ArrowLeft, Eye, Package, Plus, Save, Trash2 } from "lucide-react";
import React, { useEffect, useState } from "react";
import {
  clientsAPI,
  facturesAPI,
  prestationsAPI,
  parametresAPI,
  tachesAPI,
} from "../services/api";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import toast from "react-hot-toast";
import { useForm } from "react-hook-form";

const formatHours = (value) => `${Number(value || 0).toFixed(2)} h`;

const GENERATED_NOTES_HEADER = "Notes internes des tâches facturées :";

const roundHours = (value) => Math.round(Number(value || 0) * 100) / 100;

const buildTaskInvoiceNoteLines = (task, quantity) => {
  let hoursToInvoice = roundHours(quantity);
  const lines = [];

  const entries = [...(task.time_entries || [])].sort((a, b) =>
    String(b.created_at || "").localeCompare(String(a.created_at || ""))
  );

  for (const entry of entries) {
    const delta = roundHours(entry.delta_heures);
    if (delta <= 0) {
      continue;
    }

    if (hoursToInvoice <= 0) {
      continue;
    }

    const allocatedHours = roundHours(Math.min(delta, hoursToInvoice));
    hoursToInvoice = roundHours(hoursToInvoice - allocatedHours);

    const note = String(entry.note || "").trim();

    if (note) {
      lines.push(`${task.titre} - ${formatHours(allocatedHours)} : ${note}`);
    }
  }

  return lines.reverse();
};

const mergeInvoiceNotes = (manualNotes, generatedLines) => {
  const cleanedManualNotes = String(manualNotes || "").trim();
  const uniqueGeneratedLines = Array.from(
    new Set(generatedLines.map((line) => String(line || "").trim()).filter(Boolean))
  );

  if (uniqueGeneratedLines.length === 0) {
    return cleanedManualNotes;
  }

  const generatedNotes = [
    "Notes internes des tâches facturées :",
    ...uniqueGeneratedLines.map((line) => `- ${line}`),
  ].join("\n");

  return [cleanedManualNotes, generatedNotes].filter(Boolean).join("\n\n");
};

const stripGeneratedInvoiceNotes = (notes) => {
  const text = String(notes || "");
  const headerIndex = text.indexOf(GENERATED_NOTES_HEADER);
  if (headerIndex === -1) {
    return text;
  }
  return text.slice(0, headerIndex).trim();
};

const TaskImportModal = ({ onCancel, onConfirm, tasks, defaultRate }) => {
  const [rows, setRows] = useState(
    tasks.map((task) => ({
      taskId: task.id,
      selected: true,
      quantite: Number(task.heures_a_facturer || 0),
      max_quantite: Number(task.heures_a_facturer || 0),
      prix_unitaire: Number(task.taux_horaire ?? defaultRate ?? 0),
      task,
    }))
  );

  const updateRow = (taskId, field, value) => {
    setRows((current) =>
      current.map((row) =>
        row.taskId === taskId ? { ...row, [field]: value } : row
      )
    );
  };

  const handleConfirm = () => {
    const selectedRows = rows.filter((row) => row.selected);
    if (selectedRows.length === 0) {
      toast.error("Sélectionnez au moins une tâche");
      return;
    }

    for (const row of selectedRows) {
      const quantite = Number(row.quantite || 0);
      if (quantite <= 0) {
        toast.error(`La quantité doit être positive pour "${row.task.titre}"`);
        return;
      }
      if (quantite > Number(row.max_quantite || 0)) {
        toast.error(
          `Impossible de facturer plus de ${formatHours(
            row.max_quantite
          )} pour "${row.task.titre}"`
        );
        return;
      }
    }

    onConfirm(
      selectedRows.map((row) => ({
        taskId: row.task.id,
        description: row.task.titre,
        quantite: Number(row.quantite),
        prix_unitaire: Number(row.prix_unitaire),
        unite: "heure",
        max_quantite: Number(row.max_quantite),
        invoice_note_task: row.task,
      }))
    );
  };

  return (
    <Dialog title="Sélectionner les tâches à facturer" onClose={onCancel} size="max-w-4xl">

        <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
          <table className="min-w-[760px] divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Import
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Tâche
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Passées
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Déjà facturées
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Reste à facturer
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Quantité
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {rows.map((row) => (
                <tr key={row.taskId}>
                  <td className="px-4 py-4 align-top">
                    <input
                      type="checkbox"
                      checked={row.selected}
                      onChange={(event) =>
                        updateRow(row.taskId, "selected", event.target.checked)
                      }
                    />
                  </td>
                  <td className="px-4 py-4 align-top">
                    <div className="font-medium text-gray-900">
                      {row.task.titre}
                    </div>
                    {row.task.commentaire && (
                      <p className="mt-1 text-sm text-gray-600">
                        {row.task.commentaire}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-700">
                    {formatHours(row.task.heures_passees)}
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-700">
                    {formatHours(row.task.heures_facturees)}
                  </td>
                  <td className="px-4 py-4 text-sm font-medium text-gray-900">
                    {formatHours(row.max_quantite)}
                  </td>
                  <td className="px-4 py-4">
                    <input
                      type="number"
                      min="0"
                      max={row.max_quantite}
                      step="0.01"
                      className="input-field"
                      value={row.quantite}
                      onChange={(event) =>
                        updateRow(
                          row.taskId,
                          "quantite",
                          parseFloat(event.target.value) || 0
                        )
                      }
                      disabled={!row.selected}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col-reverse gap-3 border-t border-gray-200 p-4 sm:flex-row sm:justify-end sm:p-6">
          <button type="button" onClick={onCancel} className="btn-secondary">
            Annuler
          </button>
          <button type="button" onClick={handleConfirm} className="btn-primary">
            Ajouter à la facture
          </button>
        </div>
    </Dialog>
  );
};

const NewFacture = () => {
  const { openPDF, preview } = usePdfPreview();
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const copyId = searchParams.get("copie");
  const confirm = useConfirm();
  const [submitBusy, runSubmit] = useActionLock();
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [rectifiable, setRectifiable] = useState(true);
  const [autoEmail, setAutoEmail] = useState(null);
  const [copyReady, setCopyReady] = useState(!copyId && !id);
  const [clients, setClients] = useState([]);
  const [prestationsFrequentes, setPrestationsFrequentes] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [importableTasks, setImportableTasks] = useState([]);
  const [showImportModal, setShowImportModal] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    watch,
    setValue,
    getValues,
  } = useForm({
    defaultValues: {
      client_id: "",
      prestations: [],
      statut: "non_paye",
      notes: "",
    },
  });

  const allowNavigation = useUnsavedChanges(isDirty);

  const watchedPrestations = watch("prestations") || [];
  const watchedClientId = watch("client_id");
  const draftValue = watch();
  const { clearDraft, draftNotice } = useRecoverableDraft(`facture:${id || (copyId ? `copie:${copyId}` : "nouvelle")}`, draftValue, isDirty,
    (value) => { reset(value, { keepDefaultValues: true }); setSelectedClient(clients.find((client) => client.id === value.client_id) || null); });
  const [lineError, setLineError] = useState("");
  const validateLines = () => {
    const invalid = watchedPrestations.findIndex((line) => !String(line.description || "").trim() || !Number.isFinite(Number(line.quantite)) || Number(line.quantite) <= 0 || !Number.isFinite(Number(line.prix_unitaire)) || Number(line.prix_unitaire) < 0);
    if (invalid >= 0) { setLineError(`Complétez la description, une quantité positive et un prix valide pour la prestation ${invalid + 1}.`); return false; }
    setLineError(""); return true;
  };

  const buildGeneratedNoteLinesFromPrestations = (prestations) =>
    (prestations || []).flatMap((prestation) =>
      prestation.invoice_note_task
        ? buildTaskInvoiceNoteLines(
            prestation.invoice_note_task,
            Number(prestation.quantite || 0)
          )
        : []
    );

  const syncInvoiceNotes = (nextPrestations) => {
    const manualNotes = stripGeneratedInvoiceNotes(getValues("notes"));
    const generatedNoteLines =
      buildGeneratedNoteLinesFromPrestations(nextPrestations);
    setValue("notes", mergeInvoiceNotes(manualNotes, generatedNoteLines), {
      shouldDirty: true,
    });
  };

  useEffect(() => {
    loadPageData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if ((!id && !copyId) || clients.length === 0) return;

    const loadExisting = async () => {
      try {
        const response = await facturesAPI.getOne(id || copyId);
        const facture = response.data;
        setInvoiceNumber(facture.numero);
        setRectifiable(facture.statut === "non_paye");
        const lines = (facture.prestations || []).map((line) => ({ ...line,
          ...(copyId ? { taskId: null, invoice_note_task: undefined } : {}),
          devise: facture.client_devise || line.devise || "EUR",
        }));
        const client = clients.find((item) => item.id === facture.client_id);
        if (copyId && client && client.devise !== (facture.client_devise || "EUR")) {
          toast.error("La devise du client a changé. Vérifiez les tarifs dans la nouvelle devise.");
          lines.forEach((line) => { line.devise = client.devise; line.prix_unitaire = 0; });
        }
        reset({ client_id: facture.client_id, prestations: lines,
          statut: copyId ? "non_paye" : facture.statut || "non_paye", notes: facture.notes || "" }, copyId ? { keepDefaultValues: true } : undefined);
        setCopyReady(true);
        setSelectedClient(
          clients.find((client) => client.id === facture.client_id) || null
        );
      } catch (error) {
        toast.error("Facture introuvable");
        navigate("/factures");
      }
    };

    loadExisting();
  }, [clients, id, copyId, navigate, setValue, reset]);

  const loadPageData = async () => {
    setLoadError("");
    try {
      const [clientsResponse, prestationsResponse, settingsResponse] =
        await Promise.all([
          clientsAPI.getAll(),
          prestationsAPI.getAll(),
          parametresAPI.get(),
        ]);
      setAutoEmail(!!settingsResponse.data.email_auto_envoi);
      setClients(clientsResponse.data || []);
      setPrestationsFrequentes(prestationsResponse.data || []);
    } catch (error) {
      setLoadError("Impossible de charger les clients et les prestations. Réessayez avant de créer la facture.");
    } finally {
      setLoading(false);
    }
  };

  const addPrestation = () => {
    if (!selectedClient) { toast.error("Sélectionnez d’abord un client"); return; }
    setValue("prestations", [
      ...watchedPrestations,
      {
        description: "",
        quantite: 1,
        prix_unitaire: Number(selectedClient.taux_horaire ?? 0),
        devise: selectedClient.devise,
        unite: "heure",
      },
    ], { shouldDirty: true });
  };

  const removePrestation = (index) => {
    const nextPrestations = watchedPrestations.filter(
      (_, currentIndex) => currentIndex !== index
    );
    setValue("prestations", nextPrestations, { shouldDirty: true });
    syncInvoiceNotes(nextPrestations);
  };

  const updatePrestation = (index, field, value) => {
    const nextPrestations = [...watchedPrestations];
    nextPrestations[index] = { ...nextPrestations[index], [field]: value };
    setValue("prestations", nextPrestations, { shouldDirty: true });
    if (field === "quantite" && nextPrestations[index]?.invoice_note_task) {
      syncInvoiceNotes(nextPrestations);
    }
  };

  const handleClientChange = async (clientId) => {
    const nextClient = clients.find((client) => client.id === clientId);
    if (watchedPrestations.length && selectedClient && nextClient?.devise !== selectedClient.devise) {
      if (!await confirm("Changer de devise retirera toutes les lignes. Les tarifs ne sont pas convertis automatiquement. Continuer ?")) {
        setValue("client_id", watchedClientId);
        return false;
      }
      setValue("prestations", [], { shouldDirty: true });
      syncInvoiceNotes([]);
      setSelectedClient(nextClient || null);
      return true;
    }
    if (
      watchedPrestations.some((prestation) => prestation.taskId) &&
      watchedClientId &&
      watchedClientId !== clientId
    ) {
      const ok = await confirm(
        "Changer de client retirera les lignes importées depuis les tâches. Continuer ?"
      );
      if (!ok) {
        setValue("client_id", watchedClientId);
        return false;
      }

      const nextPrestations = watchedPrestations.filter(
        (prestation) => !prestation.taskId
      );
      setValue("prestations", nextPrestations, { shouldDirty: true });
      syncInvoiceNotes(nextPrestations);
    }
    setSelectedClient(clients.find((client) => client.id === clientId) || null);
    return true;
  };

  const addPrestationFrequente = (prestation) => {
    if (!selectedClient || (prestation.devise || "EUR") !== selectedClient.devise) { toast.error("Choisissez une prestation dans la devise du client"); return; }
    const nextPrestation = {
      devise: selectedClient.devise,
      description: prestation.description,
      quantite: prestation.quantite,
      prix_unitaire: prestation.prix_unitaire,
      unite: prestation.unite,
    };
    setValue("prestations", [...watchedPrestations, nextPrestation], { shouldDirty: true });
    toast.success("Prestation ajoutée");
  };

  const openTaskImport = async () => {
    const clientId = watchedClientId || selectedClient?.id;
    if (!clientId) {
      toast.error("Sélectionnez d'abord un client");
      return;
    }

    try {
      const response = await tachesAPI.getAFacturer(clientId);
      const existingTaskIds = new Set(
        watchedPrestations.map((prestation) => prestation.taskId).filter(Boolean)
      );
      const tasks = (response.data || []).filter(
        (task) => !existingTaskIds.has(task.id)
      );

      if (tasks.length === 0) {
        toast("Aucune tâche supplémentaire à facturer pour ce client");
        return;
      }

      setImportableTasks(tasks);
      setShowImportModal(true);
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          "Erreur lors de la récupération des tâches à facturer"
      );
    }
  };

  const confirmTaskImport = (prestations) => {
    const nextPrestations = [...watchedPrestations, ...prestations];
    setValue("prestations", nextPrestations, { shouldDirty: true });
    syncInvoiceNotes(nextPrestations);
    setShowImportModal(false);
    setImportableTasks([]);
    toast.success("Tâches ajoutées à la facture");
  };

  const calculateTotal = () =>
    watchedPrestations.reduce((total, prestation) => {
      const quantite = Number(prestation.quantite || 0);
      const prix = Number(prestation.prix_unitaire || 0);
      return total + quantite * prix;
    }, 0);

  const buildFacturePayload = (data) => {
    const validPrestations = data.prestations || [];
    const generatedNoteLines =
      buildGeneratedNoteLinesFromPrestations(validPrestations);

    return {
      client_id: data.client_id,
      prestations: validPrestations.map(
        ({ max_quantite: _max, invoice_note_task: _task, ...prestation }) => ({
          ...prestation,
          quantite: Number(prestation.quantite || 0),
          prix_unitaire: Number(prestation.prix_unitaire || 0),
          devise: prestation.devise || selectedClient?.devise || "EUR",
        })
      ),
      statut: data.statut,
      notes: mergeInvoiceNotes(
        generatedNoteLines.length > 0
          ? stripGeneratedInvoiceNotes(data.notes)
          : data.notes,
        generatedNoteLines
      ),
    };
  };

  const onSubmit = (data) => runSubmit(async () => {
    if (id && (!isDirty || !rectifiable)) return;
    if (!validateLines()) return;
    const payload = buildFacturePayload(data);
    if (payload.prestations.length === 0) { toast.error("Ajoutez au moins une prestation"); return; }
    if (copyId && !copyReady) { toast.error("Attendez le chargement de la copie"); return; }
    const ok = await confirm({
      title: id ? "Créer une facture rectificative ?" : "Créer la facture ?",
      description: id
        ? "Une nouvelle facture rectificative sera créée et l'ancienne sera annulée."
        : "La validation émet une facture et lui attribue un numéro.",
      confirmLabel: id ? "Créer la rectificative" : "Créer la facture",
      details: [["Client", selectedClient?.nom], ["Montant HT", formatMoney(calculateTotal(), selectedClient?.devise)],
        ["Facture d'origine", id ? invoiceNumber : undefined],
        ["Email", id ? "Aucun envoi automatique lors de la rectification" : autoEmail === null ? "Configuration non chargée" : autoEmail ? `Envoi automatique à ${selectedClient?.email || "adresse absente"}` : "Aucun envoi automatique"]],
    });
    if (!ok) return;

    setSaving(true);
    try {
      if (id) {
        await facturesAPI.update(id, payload);
        toast.success("Facture mise à jour avec succès");
      } else {
        await facturesAPI.create(payload);
        toast.success("Facture créée avec succès");
      }
      clearDraft();
      allowNavigation();
      navigate("/factures");
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          (id
            ? "Erreur lors de la mise à jour de la facture"
            : "Erreur lors de la création de la facture")
      );
    } finally {
      setSaving(false);
    }
  });

  const onPreview = async (data) => {
    if (!validateLines()) return;
    const payload = buildFacturePayload(data);
    if (payload.prestations.length === 0) {
      toast.error("Ajoutez au moins une prestation pour prévisualiser");
      return;
    }

    setPreviewing(true);
    try {
      const response = await facturesAPI.previewPDF(payload);
      openPDF(response, "aperçu-facture.pdf");
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          "Erreur lors de la prévisualisation de la facture"
      );
    } finally {
      setPreviewing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
        <button
          type="button"
          aria-label="Retour aux factures"
          onClick={() => navigate("/factures")}
          className="flex h-11 w-11 items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-white hover:text-gray-900"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-gray-900">
            {id ? "Créer une rectificative" : "Nouvelle facture"}
          </h1>
          <p className="text-gray-600">
            {id
              ? "Préparez le nouveau document qui remplacera cette facture"
              : "Créez une nouvelle facture pour votre client"}
          </p>
        </div>
      </div>

      {copyId && <p className="rounded-lg border border-primary-200 bg-primary-50 p-4 text-sm text-primary-900">Copie de {invoiceNumber || "la facture"} : vérifiez les lignes et les conditions avant de créer une nouvelle facture. Aucun document n'a encore été émis.</p>}
      {id && !rectifiable && <p role="status" className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">Cette facture est payée ou annulée. Elle reste consultable ; préparez une copie depuis sa fiche pour créer un nouveau document.</p>}
      {preview}
      {copyReady && draftNotice}
      <LoadError message={loadError} onRetry={loadPageData} />
      {lineError && <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-800">{lineError}</p>}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <fieldset disabled={saving || submitBusy || previewing} className="min-w-0 space-y-6">
        <div className="card">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">
            Informations de base
          </h2>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div>
              <label htmlFor="newfacture-26756" className="mb-1 block text-sm font-medium text-gray-700">
                Client *
              </label>
              <select aria-invalid={!!errors.client_id} aria-describedby={errors.client_id ? "error-newfacture-client_id" : undefined} id="newfacture-26756"
                className="input-field"
                {...register("client_id", {
                  required: "Veuillez sélectionner un client",
                })}
                onChange={async (event) => {
                  const nextId = event.target.value;
                  if (await handleClientChange(nextId)) {
                    setValue("client_id", nextId, { shouldDirty: true });
                  }
                }}
              >
                <option value="">Sélectionner un client</option>
                {clients.filter((client) => !client.archived || client.id === watchedClientId).map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.nom} {client.societe && `- ${client.societe}`} (
                    {client.devise})
                  </option>
                ))}
              </select>
              {errors.client_id && (
                <p id="error-newfacture-client_id" className="mt-1 text-sm text-red-600">
                  {errors.client_id.message}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="newfacture-28032" className="mb-1 block text-sm font-medium text-gray-700">
                Statut
              </label>
              <select id="newfacture-28032" className="input-field" {...register("statut")}>
                <option value="non_paye">En attente de paiement</option>
                <option value="paye">Payé</option>
              </select>
            </div>
          </div>

          <div className="mt-4">
            <label htmlFor="newfacture-28444" className="mb-1 block text-sm font-medium text-gray-700">
              Notes (optionnel)
            </label>
            <textarea id="newfacture-28444"
              className="input-field"
              rows={3}
              placeholder="Notes additionnelles pour la facture..."
              {...register("notes")}
            />
          </div>
        </div>

        <BillingSummary clientId={watchedClientId} />

        <div className="card">
          <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Prestations</h2>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={openTaskImport}
                className="btn-secondary flex w-full items-center justify-center gap-2 sm:w-auto"
              >
                <Plus className="h-4 w-4" />
                <span>Ajouter depuis les tâches à facturer</span>
              </button>
              <button
                type="button"
                onClick={addPrestation}
                className="btn-secondary flex w-full items-center justify-center gap-2 sm:w-auto"
              >
                <Plus className="h-4 w-4" />
                <span>Ajouter une prestation</span>
              </button>
            </div>
          </div>

          {selectedClient && (
            <div className="mb-6 rounded-lg bg-gray-50 p-4">
              <h3 className="mb-3 flex items-center text-sm font-medium text-gray-700">
                <Package className="mr-2 h-4 w-4" />
                Prestations fréquentes
              </h3>
              <p className="mb-3 text-xs text-gray-500">Prestations en {selectedClient.devise}. Aucun tarif n’est converti automatiquement.</p>
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
                {compatiblePrestations(prestationsFrequentes, selectedClient?.devise).map((prestation) => (
                  <button
                    key={prestation.id}
                    type="button"
                    onClick={() => addPrestationFrequente(prestation)}
                    className="rounded border border-gray-200 bg-white p-3 text-left transition-colors hover:border-blue-300 hover:bg-blue-50"
                  >
                    <div className="text-sm font-medium text-gray-900">
                      {prestation.description}
                    </div>
                    <div className="text-xs text-gray-600">
                      {prestation.quantite} {prestation.unite} ×{" "}
                      {formatMoney(prestation.prix_unitaire, prestation.devise)}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {watchedPrestations.map((prestation, index) => (
            <div key={index} className="mb-4 rounded-lg border border-gray-200 p-4">
              <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <h3 className="font-medium text-gray-900">
                    Prestation {index + 1}
                  </h3>
                  {prestation.taskId && (
                    <p className="text-xs text-primary-600">
                      Tâche #{prestation.taskId}
                      {prestation.max_quantite != null &&
                        ` · disponible ${formatHours(prestation.max_quantite)}`}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  aria-label={`Supprimer la prestation ${index + 1}`}
                  onClick={() => removePrestation(index)}
                  className="self-start rounded p-2 text-red-600 hover:bg-red-50 hover:text-red-900"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                <div className="md:col-span-2">
                  <label htmlFor={`newfacture-32584-${index}`} className="mb-1 block text-sm font-medium text-gray-700">
                    Description *
                  </label>
                  <input id={`newfacture-32584-${index}`}
                    required
                    type="text"
                    className="input-field"
                    value={prestation.description || ""}
                    onChange={(event) =>
                      updatePrestation(index, "description", event.target.value)
                    }
                  />
                </div>

                <div>
                  <label htmlFor={`newfacture-33126-${index}`} className="mb-1 block text-sm font-medium text-gray-700">
                    Unité
                  </label>
                  <select id={`newfacture-33126-${index}`}
                    className="input-field"
                    value={prestation.unite || "heure"}
                    onChange={(event) =>
                      updatePrestation(index, "unite", event.target.value)
                    }
                  >
                    <option value="heure">Heure</option>
                    <option value="jour">Jour</option>
                    <option value="unite">Unité</option>
                    <option value="projet">Projet</option>
                  </select>
                </div>

                <div>
                  <label htmlFor={`newfacture-33848-${index}`} className="mb-1 block text-sm font-medium text-gray-700">
                    Quantité *
                  </label>
                  <input id={`newfacture-33848-${index}`}
                    type="number"
                    step="0.01"
                    min="0"
                    max={prestation.max_quantite ?? undefined}
                    className="input-field"
                    value={prestation.quantite}
                    onChange={(event) =>
                      updatePrestation(
                        index,
                        "quantite",
                        parseFloat(event.target.value) || 0
                      )
                    }
                  />
                </div>

                <div>
                  <label htmlFor={`newfacture-34584-${index}`} className="mb-1 block text-sm font-medium text-gray-700">
                    Prix unitaire *
                  </label>
                  <input id={`newfacture-34584-${index}`}
                    type="number"
                    step="0.01"
                    min="0"
                    className="input-field"
                    value={prestation.prix_unitaire}
                    onChange={(event) =>
                      updatePrestation(
                        index,
                        "prix_unitaire",
                        parseFloat(event.target.value) || 0
                      )
                    }
                  />
                </div>

                <div className="flex items-end">
                  <div className="w-full">
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Total
                    </label>
                    <div className="input-field bg-gray-50 text-right font-medium">
                      {formatMoney(Number(prestation.quantite || 0) * Number(prestation.prix_unitaire || 0), prestation.devise || selectedClient?.devise)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}

          <div className="mt-6 border-t border-gray-200 pt-6">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <span className="text-lg font-medium text-gray-900">Total HT :</span>
              <span className="text-2xl font-bold text-primary-600">
                {formatMoney(calculateTotal(), selectedClient?.devise)}
              </span>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 z-20 flex flex-col-reverse gap-3 rounded-xl border bg-white/95 p-4 shadow-lg sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={() => navigate("/factures")}
            className="btn-secondary w-full sm:w-auto"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleSubmit(onPreview)}
            disabled={!!loadError || !!selectedClient?.archived || saving || submitBusy || previewing || !copyReady}
            className="btn-secondary flex w-full items-center justify-center gap-2 sm:w-auto"
          >
            {previewing ? (
              <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-gray-600" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
            <span>{previewing ? "Prévisualisation..." : "Prévisualiser"}</span>
          </button>
          <button
            type="submit"
            disabled={!!loadError || !!selectedClient?.archived || saving || submitBusy || previewing || !copyReady || (id && (!isDirty || !rectifiable))}
            className="btn-primary flex w-full items-center justify-center gap-2 sm:w-auto"
          >
            {saving ? (
              <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            <span>
              {saving
                ? id
                  ? "Enregistrement..."
                  : "Création..."
                : id
                ? "Créer la rectificative"
                : "Créer la facture"}
            </span>
          </button>
        </div>
        </fieldset>
      </form>

      {showImportModal && (
        <TaskImportModal
          defaultRate={selectedClient?.taux_horaire}
          tasks={importableTasks}
          onCancel={() => {
            setShowImportModal(false);
            setImportableTasks([]);
          }}
          onConfirm={confirmTaskImport}
        />
      )}
    </div>
  );
};

export default NewFacture;
